import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { adminApi } from "../../api/admin";
import type { AdminUserDetail } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatDateTime, utcTooltip } from "../../shared/format";
import { roleAtLeast, type StaffRole } from "../../shared/hooks/useStaff";
import { Alert, Badge, Button, Card, Input, Modal, Spinner } from "../../ui";
import { OrgBilling } from "./OrgBilling";

const TABS = [
  "Profile",
  "Orgs & Billing",
  "Sessions & Tokens",
  "Activity",
  "Products",
] as const;
type Tab = (typeof TABS)[number];

export function UserDetailPage() {
  const { id = "" } = useParams();
  // Inside a workspace the page shows this product's side of the person; the identity above
  // it stays global, because one Intelibo account signs in to every product (docs/14 §1).
  const { role, product } = useOutletContext<{
    role: StaffRole;
    product: string | null;
  }>();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["admin", "user", id],
    queryFn: () => adminApi.user(id),
  });
  const [tab, setTab] = useState<Tab>("Profile");
  const detailData = detail.data;
  const productsInScope = (detailData?.products ?? []).filter(
    (p) => !product || p.slug === product,
  );
  const orgsInScope = (detailData?.orgs ?? []).filter(
    (o) => !product || o.balances.some((b) => b.product === product),
  );
  const [message, setMessage] = useState<{
    kind: "success" | "error";
    text: string;
  } | null>(null);
  const [confirm, setConfirm] = useState<{
    title: string;
    action: (reason: string) => void;
    reason?: boolean;
  } | null>(null);
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState<{ notes: string; flags: string } | null>(
    null,
  );
  const canMutate = roleAtLeast(role, "admin");
  const canDelete = roleAtLeast(role, "owner");
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "user", id] });
  const run = (fn: () => Promise<unknown>, success: string) =>
    fn()
      .then(() => {
        setMessage({ kind: "success", text: success });
        setConfirm(null);
        return refresh();
      })
      .catch((err) => {
        setMessage({ kind: "error", text: errorMessage(err) });
        setConfirm(null);
      });
  const saveNotes = useMutation({
    mutationFn: () =>
      adminApi.notes(id, {
        notes: notes?.notes ?? "",
        flags: (notes?.flags ?? "").split(/[\s,]+/).filter(Boolean),
      }),
    onSuccess: () => {
      setMessage({ kind: "success", text: "Notes saved." });
      return refresh();
    },
    onError: (err) => setMessage({ kind: "error", text: errorMessage(err) }),
  });

  if (detail.isLoading) return <Spinner />;
  if (!detail.data)
    return (
      <Alert kind="error">
        {errorMessage(detail.error, "User not found.")}
      </Alert>
    );
  const d: AdminUserDetail = detail.data;
  const readOnlyTitle = canMutate ? undefined : "Read-only role";
  const actionButton = (
    label: string,
    onClick: () => void,
    variant: "secondary" | "danger" = "secondary",
    allowed = canMutate,
  ) => (
    <span
      title={allowed ? undefined : (readOnlyTitle ?? "Owner role required")}
    >
      <Button variant={variant} disabled={!allowed} onClick={onClick}>
        {label}
      </Button>
    </span>
  );

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold">{d.user.email}</h1>
          <Badge
            tone={
              d.user.status === "active"
                ? "success"
                : d.user.status === "suspended"
                  ? "warning"
                  : "danger"
            }
          >
            {d.user.status}
          </Badge>
          {d.user.email_verified ? (
            <Badge tone="success">email verified</Badge>
          ) : (
            <Badge tone="warning">unverified</Badge>
          )}
          {d.staff_role ? <Badge>staff: {d.staff_role}</Badge> : null}
          {d.user.mfa_enabled ? <Badge tone="success">MFA on</Badge> : null}
        </div>
        <p className="mt-1 text-xs text-slate-500">
          id {d.user.id} · created {formatDateTime(d.user.created_at)} · last
          login {formatDateTime(d.user.last_login_at)}
        </p>
        {message ? (
          <div className="mt-3">
            <Alert kind={message.kind}>{message.text}</Alert>
          </div>
        ) : null}
      </Card>
      <div className="flex gap-2 overflow-x-auto" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            className={`rounded-md px-3 py-1 text-sm ${tab === t ? "bg-brand-tint/50 font-medium text-brand-dark" : "text-slate-600 hover:bg-slate-100"}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "Profile" ? (
        <Card>
          <dl className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
            <dt className="text-slate-500">Name</dt>
            <dd>
              {[d.user.first_name, d.user.last_name]
                .filter(Boolean)
                .join(" ") || "—"}
            </dd>
            <dt className="text-slate-500">Country</dt>
            <dd>{d.user.country ?? "—"}</dd>
            <dt className="text-slate-500">Locale</dt>
            <dd>{d.user.locale}</dd>
            <dt className="text-slate-500">Password</dt>
            <dd>{d.user.has_password ? "set" : "none"}</dd>
          </dl>
          <h2 className="mt-4 text-sm font-semibold">Identities</h2>
          <ul className="text-sm">
            {d.identities.map((i) => (
              <li key={i.provider}>
                {i.provider} · {i.email ?? "—"} · linked{" "}
                {formatDateTime(i.linked_at)}
              </li>
            ))}
            {d.identities.length === 0 ? (
              <li className="text-slate-500">none</li>
            ) : null}
          </ul>
          <h2 className="mt-4 text-sm font-semibold">Staff notes</h2>
          <div className="mt-2 flex max-w-lg flex-col gap-2">
            <textarea
              className="rounded-md border border-slate-300 p-2 text-sm"
              rows={3}
              aria-label="Notes"
              disabled={!canMutate}
              value={notes?.notes ?? d.notes ?? ""}
              onChange={(e) =>
                setNotes({
                  notes: e.target.value,
                  flags: notes?.flags ?? d.flags.join(" "),
                })
              }
            />
            <Input
              label="Flags"
              disabled={!canMutate}
              value={notes?.flags ?? d.flags.join(" ")}
              onChange={(e) =>
                setNotes({
                  notes: notes?.notes ?? d.notes ?? "",
                  flags: e.target.value,
                })
              }
            />
            <div>{actionButton("Save notes", () => saveNotes.mutate())}</div>
          </div>
          <h2 className="mt-4 text-sm font-semibold">Actions</h2>
          <div className="mt-2 flex flex-wrap gap-2">
            {d.user.status === "suspended"
              ? actionButton("Unsuspend", () =>
                  run(() => adminApi.unsuspend(id), "User unsuspended."),
                )
              : actionButton(
                  "Suspend",
                  () =>
                    setConfirm({
                      title: "Suspend this user?",
                      reason: true,
                      action: (why) =>
                        run(() => adminApi.suspend(id, why), "User suspended."),
                    }),
                  "danger",
                )}
            {actionButton("Verify email", () =>
              run(() => adminApi.verifyEmail(id), "Email marked verified."),
            )}
            {actionButton(
              "Reset MFA",
              () => run(() => adminApi.resetMfa(id), "MFA reset."),
              "secondary",
              canMutate && d.user.mfa_enabled,
            )}
            {actionButton("Password reset link", () =>
              adminApi
                .resetLink(id)
                .then((r) =>
                  setMessage({
                    kind: "success",
                    text: `Reset link: ${r.link}`,
                  }),
                )
                .catch((err) =>
                  setMessage({ kind: "error", text: errorMessage(err) }),
                ),
            )}
            {actionButton(
              "Delete account",
              () =>
                setConfirm({
                  title: "Delete this account?",
                  action: () =>
                    run(() => adminApi.deleteUser(id), "Account deleted."),
                }),
              "danger",
              canDelete,
            )}
          </div>
        </Card>
      ) : null}
      {tab === "Orgs & Billing" ? (
        <div className="flex flex-col gap-4">
          {product ? (
            <p className="text-xs text-slate-500">
              Showing this person&apos;s {product} organisations. Their account,
              sign-in methods and sessions are the same everywhere.
            </p>
          ) : null}
          {orgsInScope.map((o) => (
            <Card key={o.org.id}>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Link
                  className="font-medium text-brand underline"
                  to={
                    product
                      ? `/admin/p/${product}/orgs/${o.org.id}`
                      : `/admin/platform/orgs/${o.org.id}`
                  }
                >
                  {o.org.name}
                </Link>
                <Badge>{o.org.kind}</Badge>
                <Badge>{o.role}</Badge>
                <span className="text-xs text-slate-500">{o.org.id}</span>
              </div>
              <OrgBilling
                orgId={o.org.id}
                balances={o.balances}
                profile={o.billing_profile}
                canMutate={canMutate}
                onChanged={() => void refresh()}
              />
            </Card>
          ))}
        </div>
      ) : null}
      {tab === "Sessions & Tokens" ? (
        <Card>
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">Sessions</h2>
            {actionButton(
              "Revoke all sessions",
              () =>
                setConfirm({
                  title: "Revoke every session?",
                  action: () =>
                    run(
                      () => adminApi.revokeAllSessions(id),
                      "Sessions revoked.",
                    ),
                }),
              "danger",
            )}
          </div>
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th>Device</th>
                <th>IP</th>
                <th>Method</th>
                <th>Last seen</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {d.sessions.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td>{s.user_agent ?? "—"}</td>
                  <td>{s.last_ip ?? "—"}</td>
                  <td>{s.amr}</td>
                  <td title={utcTooltip(s.last_seen_at)}>
                    {formatDateTime(s.last_seen_at)}
                  </td>
                  <td>
                    {s.active ? (
                      <Badge tone="success">active</Badge>
                    ) : (
                      <Badge>{s.revoked_at ? "revoked" : "expired"}</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <h2 className="mt-4 text-sm font-semibold">Personal access tokens</h2>
          <ul className="text-sm">
            {d.tokens.map((t) => (
              <li key={t.id}>
                {t.label} · {t.product} · {t.prefix}… · {t.scopes.join(" ")}{" "}
                {t.revoked_at ? <Badge>revoked</Badge> : null}
              </li>
            ))}
            {d.tokens.length === 0 ? (
              <li className="text-slate-500">none</li>
            ) : null}
          </ul>
          <h2 className="mt-4 text-sm font-semibold">MCP clients</h2>
          <ul className="text-sm">
            {d.clients.map((c) => (
              <li key={c.id}>
                {c.name} · {c.client_id} · {c.status}
              </li>
            ))}
            {d.clients.length === 0 ? (
              <li className="text-slate-500">none</li>
            ) : null}
          </ul>
        </Card>
      ) : null}
      {tab === "Activity" ? (
        <Card>
          <h2 className="mb-2 text-sm font-semibold">Login events</h2>
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th>When</th>
                <th>Method</th>
                <th>Outcome</th>
                <th>IP</th>
                <th>Country</th>
              </tr>
            </thead>
            <tbody>
              {d.login_events.map((e, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td title={utcTooltip(e.created_at)}>
                    {formatDateTime(e.created_at)}
                  </td>
                  <td>{e.method}</td>
                  <td>{e.outcome}</td>
                  <td>{e.ip ?? "—"}</td>
                  <td>{e.country ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
      {tab === "Products" ? (
        <Card>
          <ul className="text-sm">
            {productsInScope.map((p) => (
              <li key={p.slug}>
                {p.slug} · first seen {formatDateTime(p.first_seen_at)} · last
                seen {formatDateTime(p.last_seen_at)}
              </li>
            ))}
            {productsInScope.length === 0 ? (
              <li className="text-slate-500">
                {product ? `Not a ${product} user.` : "No products yet."}
              </li>
            ) : null}
          </ul>
        </Card>
      ) : null}
      <Modal
        open={!!confirm}
        title={confirm?.title ?? ""}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => confirm?.action(reason)}>
              Confirm
            </Button>
          </>
        }
      >
        {confirm?.reason ? (
          <Input
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        ) : (
          "This action is audited."
        )}
      </Modal>
    </div>
  );
}
